"use client";

import { useState } from "react";
import { Send, CheckCircle2 } from "lucide-react";
import { submitContact } from "@/actions/contact";

const fieldClass =
    "w-full border border-[#dfe5ee] bg-white px-4 py-3 text-[15px] text-[#001746] outline-none transition-colors placeholder:text-[#9aa6b2] focus:border-[#0056D2]";

export function ContactForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    const update = (key: keyof typeof form) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setForm((prev) => ({ ...prev, [key]: e.target.value }));
        if (error) setError("");
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError("");

        try {
            const result = await submitContact(form);
            if (result.success) {
                setSuccess(true);
                setForm({ name: "", email: "", phone: "", subject: "", message: "" });
            } else {
                setError(result.message);
            }
        } catch {
            setError("Something went wrong. Please try again or call us directly.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (success) {
        return (
            <div className="flex h-full min-h-[420px] flex-col items-start justify-center border border-[#dfe5ee] bg-[#f7f9fc] p-8 md:p-10">
                <CheckCircle2 className="mb-4 h-10 w-10 text-[#0056D2]" strokeWidth={1.5} />
                <h3 className="mb-2 text-2xl font-semibold text-[#001746]">Message sent</h3>
                <p className="mb-6 max-w-md text-[15px] leading-relaxed text-[#5b6b7c]">
                    Thanks for reaching out. Our team will get back to you shortly during business hours.
                </p>
                <button
                    type="button"
                    onClick={() => setSuccess(false)}
                    className="text-sm font-semibold text-[#0056D2] hover:text-[#001746]"
                >
                    Send another message
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="border border-[#dfe5ee] bg-white p-7 md:p-9">
            <h2 className="mb-2 text-2xl font-semibold text-[#001746]">Send us a message</h2>
            <p className="mb-8 text-[15px] text-[#5b6b7c]">
                Tell us what you need and we&apos;ll follow up with next steps.
            </p>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                    <label className="mb-2 block text-sm font-medium text-[#001746]">Name *</label>
                    <input
                        type="text"
                        required
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Your full name"
                        className={fieldClass}
                    />
                </div>
                <div>
                    <label className="mb-2 block text-sm font-medium text-[#001746]">Email *</label>
                    <input
                        type="email"
                        required
                        value={form.email}
                        onChange={update("email")}
                        placeholder="you@company.com"
                        className={fieldClass}
                    />
                </div>
                <div>
                    <label className="mb-2 block text-sm font-medium text-[#001746]">Phone</label>
                    <input
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="+94 7X XXX XXXX"
                        className={fieldClass}
                    />
                </div>
                <div>
                    <label className="mb-2 block text-sm font-medium text-[#001746]">Subject</label>
                    <input
                        type="text"
                        value={form.subject}
                        onChange={update("subject")}
                        placeholder="Network survey, Wi-Fi, PABX…"
                        className={fieldClass}
                    />
                </div>
            </div>

            <div className="mt-5">
                <label className="mb-2 block text-sm font-medium text-[#001746]">Message *</label>
                <textarea
                    required
                    rows={6}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Share a short brief about your site, timeline, or issue."
                    className={`${fieldClass} resize-y`}
                />
            </div>

            {error && (
                <p className="mt-4 text-sm text-red-600" role="alert">
                    {error}
                </p>
            )}

            <button
                type="submit"
                disabled={isSubmitting}
                className="mt-7 inline-flex items-center gap-2 bg-[#0056D2] px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#001746] disabled:cursor-not-allowed disabled:opacity-60"
            >
                {isSubmitting ? "Sending…" : "Send message"}
                <Send className="h-4 w-4" />
            </button>
        </form>
    );
}
